/**
 * Mobile-Friendly Scroll Utility
 * Fixes iOS Safari scrollIntoView issues
 */

/**
 * Detect if user is on iOS
 */
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

/**
 * Detect if user is on mobile (any mobile device)
 */
export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Scroll to element with iOS/mobile compatibility
 * @param {React.RefObject} ref - React ref to the element to scroll to
 * @param {Object} options - Scroll options
 * @param {number} options.delay - Delay before scrolling (ms)
 * @param {number} options.offset - Additional offset from top (px)
 * @param {boolean} options.force - Force scroll even if element not visible
 */
export const scrollToElement = (ref, options = {}) => {
  const {
    delay = isMobile() ? 300 : 400,  // Slightly longer default delay to allow rendering
    offset = isMobile() ? 80 : 80,    // Consistent offset so headers never cover content
    force = true                      // Force scroll on laptops/desktops
  } = options;

  setTimeout(() => {
    if (!ref || !ref.current) {
      // ScrollToElement: Ref not available - handled silently
      return;
    }

    try {
      const element = ref.current;
      
      // Get element position
      const elementRect = element.getBoundingClientRect();
      const elementTop = elementRect.top + window.pageYOffset;
      
      // Check if element is already visible (unless force is true)
      if (!force && elementRect.top >= 0 && elementRect.bottom <= window.innerHeight) {
        // ScrollToElement: Element already visible - handled silently
        return;
      }

      // iOS Safari-specific handling
      if (isIOS()) {
        // iOS Safari has issues with scrollIntoView, use window.scrollTo instead
        const scrollPosition = elementTop - offset;
        
        // Try smooth scroll first (works on newer iOS versions)
        try {
          window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
        } catch (e) {
          // Fallback for older iOS versions
          window.scrollTo(0, scrollPosition);
        }
      } else {
        // For non-iOS (including laptops/desktops), directly control scroll position
        const scrollPosition = elementTop - offset;
        window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('ScrollToElement error:', error);
      // Fallback: try basic scroll
      try {
        ref.current.scrollIntoView();
      } catch (e) {
        console.error('Fallback scroll failed:', e);
      }
    }
  }, delay);
};

/**
 * Scroll to top of page (useful for mobile after navigation)
 */
export const scrollToTop = () => {
  if (isIOS()) {
    // iOS-specific
    window.scrollTo(0, 0);
  } else {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
};

/**
 * Get viewport height accounting for iOS Safari address bar
 */
export const getViewportHeight = () => {
  if (isIOS()) {
    // iOS Safari: use window.innerHeight which accounts for address bar
    return window.innerHeight;
  }
  return window.innerHeight || document.documentElement.clientHeight;
};

/**
 * Check if element is in viewport
 */
export const isInViewport = (element) => {
  if (!element) return false;
  
  const rect = element.getBoundingClientRect();
  const viewportHeight = getViewportHeight();
  
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= viewportHeight &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export default {
  scrollToElement,
  scrollToTop,
  isIOS,
  isMobile,
  getViewportHeight,
  isInViewport
};

